import Footer from '../../Pages/separate/Footer'
import { Button, Card, CardContent } from '@mui/material'
import {
  BookOpen,
  TrendingUp,
  Target,
  Shield,
  BarChart3,
  Zap,
  Play,
  Calendar,
  Users,
  Award,
  ExternalLink,
  Youtube,
  ArrowRight,
  Coins,
  Rocket,
} from 'lucide-react'
import NavbarOne from '../../Pages/separate/NavbarOne'

const workshopTopics = [
  {
    icon: BookOpen,
    title: 'Blockchain Basics',
    description:
      'Understand the fundamentals of blockchain technology, decentralization, and how cryptocurrencies work.',
  },
  {
    icon: BarChart3,
    title: 'Crypto Analysis',
    description:
      'Learn how to research and evaluate cryptocurrency projects before making investment decisions.',
  },
  {
    icon: TrendingUp,
    title: 'Technical Analysis',
    description:
      'Master chart patterns, indicators, and price action to predict market movements.',
  },
  {
    icon: Target,
    title: 'Fundamental Analysis',
    description:
      'Evaluate crypto projects based on technology, team, tokenomics, and market potential.',
  },
  {
    icon: Shield,
    title: 'Risk Management',
    description:
      'Learn stop loss strategies, position sizing, and how to protect your capital.',
  },
  {
    icon: Zap,
    title: 'Entry & Exit Points',
    description:
      'Identify optimal times to enter and exit trades for maximum profitability.',
  },
]

const tradingModules = [
  {
    id: 'spot',
    title: 'Spot Trading',
    subtitle: 'Buy & Sell Instantly',
    description:
      'Buy and sell cryptocurrencies at current market prices with instant settlement. Perfect for beginners and long-term investors.',
    features: [
      'Market Orders',
      'Limit Orders',
      'Order Book Analysis',
      'Trading Pairs',
    ],
    icon: Coins,
    color: 'from-primary/20 to-primary/5',
    borderColor: 'border-primary/30',
    stats: { risk: 'Low', leverage: '1x', settlement: 'Instant' },
  },
  {
    id: 'futures',
    title: 'Futures Trading',
    subtitle: 'Amplify Your Gains',
    description:
      'Trade cryptocurrency derivatives with leverage for amplified gains. For experienced traders seeking higher returns.',
    features: [
      'Leverage Trading',
      'Long & Short Positions',
      'Margin Management',
      'Liquidation Prevention',
    ],
    icon: Rocket,
    color: 'from-accent/20 to-accent/5',
    borderColor: 'border-accent/30',
    stats: { risk: 'High', leverage: 'Up to 125x', settlement: 'Contract' },
  },
]

const Training = (props) => {
  return (
    <div className='min-h-screen bg-background'>
      <NavbarOne setTheme={props.setTheme} />

      <section className='relative pt-32 pb-20 overflow-hidden'>
        <div className='absolute inset-0 bg-hero-glow pointer-events-none' />
        <div className='absolute top-20 right-0 w-1/3 h-1/3 bg-gradient-radial from-primary/10 via-transparent to-transparent pointer-events-none' />

        <div className='container mx-auto px-4 relative z-10'>
          <div className='max-w-4xl mx-auto text-center'>
            <div className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 border border-border mb-6'>
              <BookOpen className='h-4 w-4 text-primary' />
              <span className='text-sm text-muted-foreground'>
                Education Hub
              </span>
            </div>

            <h1 className='font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6'>
              Master Crypto Trading with{' '}
              <span className='gradient-text'>Expert Guidance</span>
            </h1>

            <p className='text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8'>
              Join our one-day workshops and learn everything from blockchain
              basics to advanced trading strategies. Plus, access free
              educational content on our YouTube channel.
            </p>

            <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
              <Button variant='hero' size='lg' className='group'>
                <Calendar className='h-5 w-5 mr-2' />
                Register for Workshop
              </Button>
              <Button variant='hero-outline' size='lg' asChild>
                <a
                  href='https://www.youtube.com/@UniticExchange2025'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <Youtube className='h-5 w-5 mr-2' />
                  Watch on YouTube
                </a>
              </Button>
            </div>
          </div>

          <div className='grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl mx-auto mt-16'>
            {[
              { value: '50+', label: 'Workshops Conducted' },
              { value: '5000+', label: 'Students Trained' },
              { value: '100+', label: 'Video Tutorials' },
              { value: '4.9', label: 'Average Rating' },
            ].map((stat) => (
              <div key={stat.label} className='text-center'>
                <div className='text-2xl sm:text-3xl font-display font-bold text-foreground'>
                  {stat.value}
                </div>
                <div className='text-sm text-muted-foreground mt-1'>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className='py-20 bg-card/30'>
        <div className='container mx-auto px-4'>
          <div className='text-center mb-12'>
            <h2 className='font-display text-3xl sm:text-4xl font-bold mb-4'>
              What You'll Learn in Our{' '}
              <span className='gradient-text'>Workshops</span>
            </h2>
            <p className='text-muted-foreground max-w-2xl mx-auto'>
              Our comprehensive one-day workshops cover everything you need to
              start trading confidently.
            </p>
          </div>

          <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto'>
            {workshopTopics.map((topic) => (
              <Card
                key={topic.title}
                className='bg-card/50 border-border hover:border-primary/50 transition-all group'
              >
                <CardContent className='p-6'>
                  <div className='w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors'>
                    <topic.icon className='h-6 w-6 text-primary' />
                  </div>
                  <h3 className='font-display text-xl font-semibold mb-2'>
                    {topic.title}
                  </h3>
                  <p className='text-muted-foreground text-sm'>
                    {topic.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className='py-20 relative overflow-hidden'>
        <div className='absolute inset-0 pointer-events-none'>
          <div className='absolute top-1/4 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl' />
          <div className='absolute bottom-1/4 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl' />
        </div>

        <div className='container mx-auto px-4 relative z-10'>
          <div className='text-center mb-16'>
            <div className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 border border-border mb-6'>
              <BarChart3 className='h-4 w-4 text-primary' />
              <span className='text-sm text-muted-foreground'>
                Master Both Methods
              </span>
            </div>
            <h2 className='font-display text-3xl sm:text-4xl font-bold mb-4'>
              Trading <span className='gradient-text'>Modules</span>
            </h2>
            <p className='text-muted-foreground max-w-2xl mx-auto'>
              Deep dive into different trading methods with hands-on practice
              sessions.
            </p>
          </div>

          <div className='max-w-6xl mx-auto'>
            <div className='grid lg:grid-cols-2 gap-8'>
              {tradingModules.map((module) => (
                <div
                  key={module.id}
                  className={`group relative rounded-3xl bg-gradient-to-br ${module.color} border ${module.borderColor} p-1 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-primary/10`}
                >
                  <div className='relative rounded-[1.4rem] bg-card/80 backdrop-blur-sm p-8 h-full overflow-hidden'>
                    <div className='absolute inset-0 opacity-5'>
                      <div
                        className='absolute inset-0'
                        style={{
                          backgroundImage:
                            'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
                          backgroundSize: '24px 24px',
                        }}
                      />
                    </div>

                    <div
                      className={`absolute -top-6 -right-6 w-32 h-32 rounded-full bg-gradient-to-br ${module.color} opacity-50 blur-2xl group-hover:opacity-80 transition-opacity duration-500`}
                    />

                    <div className='relative z-10'>
                      <div className='flex items-start justify-between mb-6'>
                        <div>
                          <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/50 border border-border mb-3'>
                            <module.icon className='h-4 w-4 text-primary' />
                            <span className='text-xs font-medium text-muted-foreground'>
                              {module.subtitle}
                            </span>
                          </div>
                          <h3 className='font-display text-2xl sm:text-3xl font-bold'>
                            {module.title}
                          </h3>
                        </div>
                        <div className='w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300'>
                          <module.icon className='h-7 w-7 text-primary' />
                        </div>
                      </div>

                      <p className='text-muted-foreground mb-6'>
                        {module.description}
                      </p>

                      <div className='grid grid-cols-3 gap-3 mb-6'>
                        {Object.entries(module.stats).map(([key, value]) => (
                          <div
                            key={key}
                            className='bg-secondary/30 rounded-xl p-3 text-center border border-border/50'
                          >
                            <div className='text-xs text-muted-foreground uppercase tracking-wider mb-1'>
                              {key}
                            </div>
                            <div className='text-sm font-semibold text-foreground'>
                              {value}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className='space-y-2 mb-6'>
                        {module.features.map((feature, i) => (
                          <div
                            key={feature}
                            className='flex items-center gap-3 p-2 rounded-lg bg-secondary/20 border border-transparent hover:border-primary/20 transition-colors'
                          >
                            <div className='w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0'>
                              <span className='text-xs font-bold text-primary'>
                                {i + 1}
                              </span>
                            </div>
                            <span className='text-sm'>{feature}</span>
                          </div>
                        ))}
                      </div>

                      <Button
                        variant='ghost'
                        className='w-full justify-between group/btn hover:bg-primary/10 border border-border'
                      >
                        <span>Learn {module.title}</span>
                        <ArrowRight className='h-4 w-4 group-hover/btn:translate-x-1 transition-transform' />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className='flex items-center justify-center mt-12'>
              <div className='flex items-center gap-4 px-6 py-3 rounded-full bg-secondary/30 border border-border'>
                <div className='flex items-center gap-2'>
                  <div className='w-3 h-3 rounded-full bg-primary' />
                  <span className='text-sm text-muted-foreground'>
                    Beginner Friendly
                  </span>
                </div>
                <div className='w-px h-4 bg-border' />
                <div className='flex items-center gap-2'>
                  <div className='w-3 h-3 rounded-full bg-accent' />
                  <span className='text-sm text-muted-foreground'>
                    Advanced Traders
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='py-20 bg-card/30'>
        <div className='container mx-auto px-4'>
          <div className='max-w-6xl mx-auto'>
            <div className='grid lg:grid-cols-2 gap-12 items-center'>
              <div>
                <div className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 mb-6'>
                  <Youtube className='h-4 w-4 text-red-500' />
                  <span className='text-sm text-red-400'>YouTube Channel</span>
                </div>

                <h2 className='font-display text-3xl sm:text-4xl font-bold mb-4'>
                  Free Education on{' '}
                  <span className='text-red-500'>YouTube</span>
                </h2>

                <p className='text-muted-foreground mb-6'>
                  Can't attend a workshop? No problem! We continuously educate
                  the crypto community through our YouTube channel with regular
                  video tutorials, market analysis, and trading strategies.
                </p>

                <ul className='space-y-3 mb-8'>
                  {[
                    'Weekly market analysis videos',
                    'Step-by-step trading tutorials',
                    'Live trading sessions',
                    'Beginner-friendly content',
                  ].map((item) => (
                    <li key={item} className='flex items-center gap-3'>
                      <div className='w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center'>
                        <Play className='h-3 w-3 text-primary' />
                      </div>
                      <span className='text-sm'>{item}</span>
                    </li>
                  ))}
                </ul>

                <Button variant='hero' size='lg' asChild>
                  <a
                    href='https://www.youtube.com/@UniticExchange2025'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='group'
                  >
                    <Youtube className='h-5 w-5 mr-2' />
                    Subscribe to Channel
                    <ExternalLink className='h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform' />
                  </a>
                </Button>
              </div>

              <div className='relative'>
                <div className='aspect-video rounded-2xl overflow-hidden border border-border bg-card/50'>
                  <iframe
                    className='w-full h-full'
                    src='https://www.youtube.com/embed?listType=user_uploads&list=UniticExchange2025'
                    title='Unitic Exchange YouTube Channel'
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                  />
                </div>

                <div className='absolute -top-4 -right-4 w-24 h-24 bg-red-500/10 rounded-full blur-2xl' />
                <div className='absolute -bottom-4 -left-4 w-32 h-32 bg-primary/10 rounded-full blur-2xl' />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='py-20'>
        <div className='container mx-auto px-4'>
          <div className='max-w-4xl mx-auto'>
            <Card className='bg-gradient-to-br from-primary/10 via-card to-secondary/20 border-primary/20 overflow-hidden relative'>
              <div className='absolute top-0 right-0 w-1/2 h-full bg-gradient-radial from-primary/5 via-transparent to-transparent' />

              <CardContent className='p-8 sm:p-12 relative z-10'>
                <div className='grid md:grid-cols-2 gap-8 items-center'>
                  <div>
                    <h2 className='font-display text-3xl sm:text-4xl font-bold mb-4'>
                      Ready to Start Your{' '}
                      <span className='gradient-text'>Trading Journey?</span>
                    </h2>
                    <p className='text-muted-foreground mb-6'>
                      Join our next workshop and learn from industry experts.
                      Limited seats available!
                    </p>

                    <div className='flex flex-col sm:flex-row gap-4'>
                      <Button variant='hero' size='lg'>
                        <Calendar className='h-5 w-5 mr-2' />
                        Book Workshop Seat
                      </Button>
                    </div>
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div className='bg-card/50 rounded-xl p-4 border border-border'>
                      <Users className='h-8 w-8 text-primary mb-2' />
                      <div className='text-2xl font-bold'>30</div>
                      <div className='text-sm text-muted-foreground'>
                        Seats per batch
                      </div>
                    </div>
                    <div className='bg-card/50 rounded-xl p-4 border border-border'>
                      <Award className='h-8 w-8 text-primary mb-2' />
                      <div className='text-2xl font-bold'>100%</div>
                      <div className='text-sm text-muted-foreground'>
                        Hands-on training
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Training
